import { z } from 'zod'

export const MATCH_PHASES = ['group', 'round_of_32', 'round_of_16', 'quarterfinal', 'semifinal', 'third_place', 'final'] as const
/** Le fasi con nome; oltre i sedicesimi si usa `knockout_round_N`. */
export type MatchPhase = (typeof MATCH_PHASES)[number] | `knockout_round_${number}`

export const MATCH_STATUSES = ['scheduled', 'live', 'completed'] as const
export type MatchStatus = (typeof MATCH_STATUSES)[number]

export type Match = {
  id: string
  tournamentId: string
  phase: MatchPhase
  round: number
  sortOrder: number
  homeTeamId: string
  awayTeamId: string
  homeScore: number | null
  awayScore: number | null
  status: MatchStatus
  timerStartedAt: string | null
  timerElapsed: number
  timerRunning: boolean
  completedAt: string | null
}

const id = z.string().trim().min(1).max(64)
// Il server usa il proprio timer; il campo resta facoltativo per i client che lo mandano ancora.
const second = z.number().int().min(0).max(86_400).optional()

/** Bicchiere preso (o tiro del pareggio se `isRedemption`): chi segna, a quale squadra, quale bicchiere; il secondo lo prende il server dal timer. */
export const CupEventRequestSchema = z.object({
  scorerId: id,
  targetTeamId: id,
  cupIndex: z.number().int().min(0).max(5),
  second,
  isRedemption: z.boolean().default(false),
})
export type CupEventRequest = z.infer<typeof CupEventRequestSchema>

export type CupEvent = Omit<CupEventRequest, 'second'> & {
  second: number
  id: string
  matchId: string
  isRedeemed: boolean
  redeemedByEventId: string | null
  createdAt: string
}

/** Spostamento bicchieri: la squadra richiedente sposta i bicchieri ancora attivi dell'avversario in posizioni vuote. */
export const RearrangementRequestSchema = z.object({
  requestingTeamId: id,
  targetTeamId: id,
  moves: z.array(z.object({ from: z.number().int().min(0).max(5), to: z.number().int().min(0).max(5) })).min(1).max(6),
  second,
})
export type RearrangementRequest = z.infer<typeof RearrangementRequestSchema>
export type CupRearrangement = Omit<RearrangementRequest, 'second'> & { second: number; id: string; matchId: string; createdAt: string }

/** Airball ("tiro della vergogna"): il tiro non tocca nemmeno il tavolo; la squadra la ricava il server. */
export const AirballRequestSchema = z.object({ playerId: id, second })
export type AirballRequest = z.infer<typeof AirballRequestSchema>
export type AirballEvent = Omit<AirballRequest, 'second'> & { second: number; id: string; matchId: string; teamId: string; createdAt: string }

export const TimerActionSchema = z.object({ action: z.enum(['start', 'stop', 'reset']) })
export type TimerAction = z.infer<typeof TimerActionSchema>

/** GET /api/tournaments/:id/matches/:matchId: partita, squadre con giocatori, eventi e settaggi rilevanti. */
export type MatchDetail = {
  match: Match
  homeTeam: { id: string; name: string; players: { id: string; name: string }[] }
  awayTeam: { id: string; name: string; players: { id: string; name: string }[] }
  cupEvents: CupEvent[]
  rearrangements: CupRearrangement[]
  airballs: AirballEvent[]
  settings: { cupsPerTeamGroup: number; cupsPerTeamFinal: number; redemptionShot: boolean; cupRearrangementAllowed: boolean; airballEnabled: boolean }
  maxCups: number
}
